import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { convertGCSPathToURL } from '../../../services/storageService'
import type { ViewingDocument } from '../types'
import type { CaseDocument } from '../../../hooks/useCase'

export interface UseDocumentViewerResult {
  viewingDocument: ViewingDocument | null
  isLoadingDoc: boolean
  handleViewDocument: (doc: CaseDocument) => Promise<void>
  handleCloseViewer: () => void
}

export function useDocumentViewer(
  caseId: string | undefined,
  documents: CaseDocument[] | undefined,
  caseLoading: boolean
): UseDocumentViewerResult {
  const { documentId } = useParams<{ documentId?: string }>()
  const navigate = useNavigate()
  const [viewingDocument, setViewingDocument] = useState<ViewingDocument | null>(null)
  const [isLoadingDoc, setIsLoadingDoc] = useState(false)

  // Sync document viewer state with URL (for deep linking / refresh persistence)
  useEffect(() => {
    // If no documentId in URL, clear the viewer
    if (!documentId) {
      setViewingDocument(null)
      return
    }
    
    // Wait for case data to load
    if (!documents || caseLoading) return
    
    const doc = documents.find(d => d.id === documentId)
    if (!doc) return
    
    // Load the document
    const loadDoc = async () => {
      setIsLoadingDoc(true)
      try {
        let url: string
        if (doc.downloadUrl) {
          url = doc.downloadUrl
        } else if (doc.storagePath) {
          url = await convertGCSPathToURL(doc.storagePath)
        } else {
          throw new Error('No URL or storage path available')
        }
        setViewingDocument({ url, filename: doc.filename, docId: doc.id })
      } catch (error) {
        console.error('Failed to load document from URL:', error)
      } finally {
        setIsLoadingDoc(false)
      }
    }
    loadDoc()
  }, [documentId, documents, caseLoading])

  // View uploaded document - prefer downloadUrl, fallback to converting storagePath
  const handleViewDocument = async (doc: CaseDocument) => {
    setIsLoadingDoc(true)
    try {
      let url: string

      // Prefer downloadUrl (pre-signed from backend) over storagePath
      if (doc.downloadUrl) {
        url = doc.downloadUrl
      } else if (doc.storagePath) {
        url = await convertGCSPathToURL(doc.storagePath)
      } else {
        throw new Error('No URL or storage path available')
      }

      setViewingDocument({ url, filename: doc.filename, docId: doc.id })
      
      // Navigate to document URL for deep linking
      if (caseId && doc.id) {
        setTimeout(() => {
          navigate(`/quick-check/${caseId}/documents/${doc.id}`)
        }, 0)
      }
    } catch (error) {
      console.error('Failed to load document:', error)
    } finally {
      setIsLoadingDoc(false)
    }
  }

  // Close document viewer - navigate back to documents list
  const handleCloseViewer = () => {
    if (caseId) {
      navigate(`/quick-check/${caseId}/documents`)
    }
  }

  return {
    viewingDocument,
    isLoadingDoc,
    handleViewDocument,
    handleCloseViewer,
  }
}

